import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export type UserRole = "admin" | "contributor" | "viewer";

export interface UserPreferences {
  assets_default_view?: "table" | "map" | "layout";
  assets_table_columns?: string[];
  assets_table_sort_column?: string | null;
  assets_table_sort_direction?: "asc" | "desc" | null;
  show_own_activities?: boolean;
  activity_last_seen_at?: string | null;
}

export interface Profile {
  id: string;
  user_id: string;
  email: string;
  name: string | null;
  role: UserRole;
  org_id: string | null;
  preferences?: UserPreferences;
  created_at: string;
  updated_at: string;
}

export interface Organization {
  id: string;
  name: string;
  country: string;
  timezone: string;
  language: string;
  logo_url: string | null;
  logo_link: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null | undefined;
  organization: Organization | null | undefined;
  loading: boolean;
  profileLoading: boolean;
  organizationLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  signInWithMicrosoft: () => Promise<{ error: Error | null }>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Fetch user profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id || !user?.email) return null;

      // Try to fetch existing profile
      const { data, error } = await supabase.from("profiles").select("*").eq("user_id", user.id).maybeSingle();

      // If profile exists, return it
      if (data) return data as Profile;

      // If profile doesn't exist (not an actual error), create it
      if (error && error.code !== "PGRST116") throw error;

      // Create new profile automatically
      const { data: newProfile, error: createError } = await supabase
        .from("profiles")
        .insert({
          user_id: user.id,
          email: user.email,
          name: user.user_metadata?.name || user.user_metadata?.full_name || user.email,
          org_id: null,
          role: "viewer",
        })
        .select()
        .single();

      if (createError) throw createError;
      return newProfile as Profile;
    },
    enabled: !!user?.id,
    retry: 1,
  });

  // Fetch organization
  const { data: organization, isLoading: organizationLoading } = useQuery({
    queryKey: ["organization", profile?.org_id],
    queryFn: async () => {
      if (!profile?.org_id) return null;
      const { data, error } = await supabase.from("orgs").select("*").eq("id", profile.org_id).single();

      if (error) throw error;
      return data as Organization;
    },
    enabled: !!profile?.org_id,
  });

  // Clear corrupted auth state
  const clearAuthState = () => {
    try {
      localStorage.removeItem("supabase.auth.token");
      sessionStorage.clear();
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith("sb-")) {
          localStorage.removeItem(key);
        }
      });
      console.log("Cleared corrupted auth state");
    } catch (error) {
      console.error("Error clearing auth state:", error);
    }
  };

  // Safety timeout - if auth doesn't resolve in 5 seconds, stop loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener - IMPORTANT: NOT async to prevent deadlocks
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      setSession(session);
      setUser(session?.user ?? null);

      if (event === "SIGNED_OUT") {
        queryClient.clear();
      }
      
      setLoading(false);
    });

    // Get initial session
    const getInitialSession = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        if (!mounted) return;

        if (error) {
          if (error.message?.includes('missing email or phone')) {
            clearAuthState();
          } else {
            console.error("Error getting session:", error);
            clearAuthState();
          }
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      } catch (error: any) {
        if (error?.message?.includes('missing email or phone')) {
          clearAuthState();
        } else {
          console.error("Error getting session:", error);
          clearAuthState();
        }
        if (!mounted) return;
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    };

    getInitialSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, name?: string) => {
    // Check if email is in the whitelist using secure edge function
    try {
      const { data: checkResult, error: checkError } = await supabase.functions.invoke('check-email-allowed', {
        body: { email: email.toLowerCase() }
      });

      if (checkError || !checkResult?.allowed) {
        return {
          error: new Error("This app is currently in private alpha. Please contact info@autelion.com for access."),
        };
      }
    } catch (error) {
      console.error('Error checking email whitelist:', error);
      return {
        error: new Error("Unable to verify email. Please try again later."),
      };
    }

    // Proceed with signup if whitelisted
    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: name ? { name } : undefined,
      },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user?.id) return { error: new Error("No user") };

    // Convert preferences to JSON-compatible format
    const dbUpdates = {
      ...updates,
      preferences: updates.preferences ? JSON.parse(JSON.stringify(updates.preferences)) : undefined,
    };

    const { error } = await supabase.from("profiles").update(dbUpdates).eq("user_id", user.id);

    if (!error) {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    }

    return { error };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  const signInWithMicrosoft = async () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "azure",
      options: {
        redirectTo: redirectUrl,
        scopes: "email",
      },
    });

    if (error) {
      console.error("OAuth error:", error);
      clearAuthState();
    }

    return { error };
  };

  const value: AuthContextValue = {
    session,
    user,
    profile,
    organization,
    loading,
    profileLoading,
    organizationLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    updatePassword,
    signInWithMicrosoft,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
